import PropTypes, { func, instanceOf } from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { Divider, Header, Item } from "semantic-ui-react";
import { FormattedMessage } from "react-intl";
import { withRouter } from "../../../helpers/withRouter";
import { getAccount, getUserRole } from "../../../selectors";
import { getRequiredUpdates } from "../../../selectors/unit/subLineItemRules";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import { RoomBookController } from "../../../actions/roomBookController";
import "./subLineItemPicker.scss";
import { getSubLineItemAdjustmentDeltaForProductLine } from "../../../selectors/unit/productLinesImpact";
import RequiredRuleUpdates from "./requiredRuleUpdates";
import RequiredProductLineUpdates from "./requiredProductLineUpdates";
import { SubLineItemShape } from "../../configurator/roomBook.shape";
import FormatCurrency from "../../currency/FormatCurrency";
import { defaultRichTextTags } from "../../../../builder_portal/helpers/defaultRichTextTags";
import { Account } from "../../../models/account";
import { getProductLineScopeForTags } from "../../../selectors/unit/productLines";
import AdminMessageDialog from "../AdminMessageDialog";

class SubLineItemPicker extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { showAdminMessage: false, loadingId: null };
  }

  componentDidMount = () => {
    const { subLineItem } = this.props;
    if (subLineItem.isAutoDecidable) {
      this.toggleSelection(subLineItem.validOptions[0]);
    }
  };

  handleClose = () => {
    this.setState({ showAdminMessage: false });
  };

  onlyAdminOrGuest(action) {
    const { userRole } = this.props;
    if (["admin", "buyer"].includes(userRole)) {
      return action();
    }
    this.setState({ showAdminMessage: true });
    return Promise.resolve();
  }

  toggleSelection = product => {
    const { roomBookController, subLineItem } = this.props;
    return this.onlyAdminOrGuest(() => {
      if (product.isBuyerSelection) {
        return roomBookController
          .deselectProducts([subLineItem.id])
          .then(() => {
            this.setState({ loadingId: null });
          });
      }

      return this.confirmedRuleSelection(subLineItem.id, product.id)
        .then(accumulatedProductSelection => {
          return this.confirmedProductLineSelection(
            product,
            accumulatedProductSelection,
            subLineItem
          );
        })
        .then(accumulatedProductSelection => {
          return accumulatedProductSelection
            ? roomBookController.selectProducts(accumulatedProductSelection)
            : Promise.resolve();
        })
        .then(apiResponse => {
          this.setState({ loadingId: null, confirmationDialog: null });
          return apiResponse;
        });
    });
  };

  confirmedProductLineSelection = (
    selectedProduct,
    accumulatedProductSelection,
    selectedSubLineItem
  ) => {
    return new Promise(resolve => {
      const {
        getRequiredProductLineUpdates,
        getScopeForTags,
        account
      } = this.props;
      const productLineUpdatesWithDestroyed = getRequiredProductLineUpdates(
        selectedProduct?.productTags
      );
      const productLineUpdates = productLineUpdatesWithDestroyed
        ?.filter(({ newValidOption }) => !!newValidOption)
        .filter(({ subLineItem }) => {
          const productLineScope = getScopeForTags(
            subLineItem.product_group_id,
            selectedProduct?.productTags
          );
          let keepElement;
          switch (productLineScope) {
            case "section":
              keepElement =
                subLineItem.section_id === selectedSubLineItem.section_id;
              break;
            case "sub_section":
              keepElement =
                subLineItem.sub_section_id ===
                selectedSubLineItem.sub_section_id;
              break;
            default:
              // case "roombook":
              keepElement =
                subLineItem.room_book_id === selectedSubLineItem.room_book_id;
              break;
          }
          return keepElement;
        });

      if (
        productLineUpdates?.length > 1 &&
        account.isEnabled("product_line_macro")
      ) {
        const buttons = [
          {
            id: "ok",
            icon: "heart outline",
            color: "primary", // goes into className prop, not color...
            label:
              "buyerPortal.productLineUpdate.confirmationDialog.actionLabel",
            onClick: () => {
              const additionalSelection = {};
              productLineUpdates.forEach(({ subLineItem, newValidOption }) => {
                additionalSelection[subLineItem.id] =
                  newValidOption?.id ?? "_destroy";
              });
              resolve({
                ...accumulatedProductSelection,
                ...additionalSelection
              });
            }
          },
          {
            id: "cancel",
            label: "meta.actions.cancel",
            basic: true,
            onClick: () => {
              resolve({});
            }
          }
        ];
        const totalTotal = productLineUpdates.reduce(
          (totalSum, { newValidOption, oldInvalidOption }) =>
            totalSum +
            ((newValidOption?.total ?? 0) - (oldInvalidOption?.total ?? 0)),
          0
        );
        const isOnRequest = productLineUpdates.reduce(
          (accu, { newValidOption }) => accu || newValidOption?.isOnRequest,
          false
        );

        const confirmationDialog = (
          <ConfirmationDialog
            scrollContent
            open
            onAsyncClose={() => {
              this.setState({ confirmationDialog: null });
            }}
            title={
              <FormattedMessage
                id="buyerPortal.productLineUpdate.confirmationDialog.title"
                defaultMessage="Wechsel der Ausstattungslinie"
              />
            }
            content={
              <RequiredProductLineUpdates
                productLineUpdates={productLineUpdates}
              />
            }
            extra={
              <>
                <Divider />
                <Item key="footer">
                  <Item.Content>
                    <Item.Header>
                      <Header textAlign="right" className="totalHeader">
                        {totalTotal >= 0 ? (
                          <FormattedMessage
                            id="buyerPortal.productLineUpdate.confirmationDialog.totalDebit"
                            defaultMessage="Gesamt Mehrkosten: +"
                          />
                        ) : (
                          <FormattedMessage
                            id="buyerPortal.productLineUpdate.confirmationDialog.totalCredit"
                            defaultMessage="Gesamt Minderkosten:"
                          />
                        )}
                        &nbsp;
                        <FormatCurrency
                          className={totalTotal >= 0 ? "debit" : "credit"}
                          amount={totalTotal}
                          isOnRequest={isOnRequest}
                        />
                      </Header>
                    </Item.Header>
                    <Item.Description>
                      <FormattedMessage
                        id="buyerPortal.productLineUpdate.confirmationDialog.actionHint"
                        values={defaultRichTextTags}
                      />
                    </Item.Description>
                  </Item.Content>
                </Item>
              </>
            }
            buttons={buttons}
          />
        );

        this.setState({
          loadingId: selectedProduct.id,
          confirmationDialog
        });
      } else {
        resolve({ ...accumulatedProductSelection });
      }
    });
  };

  /*
   * Background:
   * If the buyer changes the selection and the other selections might become
   * invalid, those selections should be reset. Of course, the buyer should
   * confirm resetting other selections beforehand.
   */
  confirmedRuleSelection = (subLineItemId, productId) => {
    const { getRequiredRuleUpdates } = this.props;
    this.setState({ loadingId: productId });
    return new Promise(resolve => {
      const currentSelection = { [subLineItemId]: { product_id: productId } };
      const updates = getRequiredRuleUpdates(subLineItemId, productId);

      if (!updates.items.length) {
        resolve(currentSelection);
      } else {
        const buttons = [
          {
            id: "ok",
            label: "meta.actions.proceedAnyway",
            basic: true,
            onClick: cb => {
              const additionalSelection = updates.items.reduce((accu, u) => {
                if (u.item.isOptional) {
                  accu[u.item.id] = "_destroy";
                } else {
                  accu[u.item.id] = {
                    product_id: null,
                    quantity: u.item.quantityBuyerSelection
                  };
                }
                return accu;
              }, {});

              resolve({
                ...currentSelection,
                ...additionalSelection
              });
              cb();
            }
          },
          {
            id: "cancel",
            label: "meta.actions.cancel",
            basic: true,
            onClick: cb => {
              this.setState({ loadingId: null }, () => {
                resolve({});
                cb();
              });
            }
          }
        ];
        const confirmationDialog = (
          <ConfirmationDialog
            open
            onAsyncClose={() => {
              this.setState({ confirmationDialog: null });
            }}
            title="buyerPortal.roomBook.buyerSelection.confirmation.title"
            content={<RequiredRuleUpdates requiredUpdates={updates} />}
            buttons={buttons}
          />
        );

        this.setState({
          confirmationDialog
        });
      }
    });
  };

  render = () => {
    const { children } = this.props;
    const { loadingId, showAdminMessage, confirmationDialog } = this.state;

    return (
      <>
        <AdminMessageDialog
          showAdminMessage={showAdminMessage}
          handleClose={this.handleClose}
        />
        {confirmationDialog}
        {children?.({
          select: this.toggleSelection,
          loadingId
        })}
      </>
    );
  };
}

const mapStateToProps = state => {
  return {
    userRole: getUserRole(state),
    getRequiredRuleUpdates: getRequiredUpdates(state),
    getRequiredProductLineUpdates: getSubLineItemAdjustmentDeltaForProductLine(
      state
    ),
    getScopeForTags: getProductLineScopeForTags(state),
    account: getAccount(state)
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const { id: unitId } = props.params;
  const roomBookController = new RoomBookController(dispatch, unitId);
  return {
    roomBookController
  };
};

SubLineItemPicker.propTypes = {
  roomBookController: instanceOf(RoomBookController).isRequired,
  subLineItem: SubLineItemShape.isRequired,
  getRequiredRuleUpdates: func.isRequired,
  getRequiredProductLineUpdates: func.isRequired,
  getScopeForTags: func.isRequired,
  children: func.isRequired,
  userRole: PropTypes.string.isRequired,
  account: instanceOf(Account).isRequired
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SubLineItemPicker)
);
